import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import { resolveGoogleDocGenUserId } from "./utils/googleDocGenUserId";
import {
  fetchPublicStudentPackage,
  isRegistrationPortalRpcUnavailable,
} from "./utils/registrationPortalApi";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Text,
  useToast,
  VStack,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Container,
  Center,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
  Badge,
} from "@chakra-ui/react";
import { InvoicePdfModal } from "./components/InvoicePdfModal";
import { CheckCircleIcon, ViewIcon, DownloadIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import moment from "moment";
import { FaFileAlt, FaFilePdf, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUser, FaGraduationCap, FaBook, FaInfoCircle, FaChalkboardTeacher, FaCheckCircle, FaHourglassHalf, FaCalendarPlus, FaSignature } from "react-icons/fa";

export const StudentPackage = () => {
  const { registrationID: _rid, id: _id } = useParams();
  const registrationID = _rid || _id; // works under both /student-package/:registrationID and /register/:id
  const location = useLocation();
  const [registrationData, setRegistrationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeDocument, setActiveDocument] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadedDocs, setDownloadedDocs] = useState(new Set()); // Track which documents have been downloaded
  const [isPolling, setIsPolling] = useState(false);
  const [currentDocCount, setCurrentDocCount] = useState(0);
  const [expandedModules, setExpandedModules] = useState(new Set()); // Track which modules are expanded
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false); // Show payment success alert
  const [orgGstSettings, setOrgGstSettings] = useState({ gst_enabled: false, gst_rate: 0 }); // Organization GST settings
  const pollIntervalRef = useRef(null);
  const hasTriggeredGenerationRef = useRef(false);
  
  // Check for payment success URL parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('payment') === 'success') {
      setShowPaymentSuccess(true);
      // Remove the query params from URL without refreshing
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.search, registrationID]);

  // Fetch organization GST settings
  useEffect(() => {
    const fetchOrganizationGstSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('organization')
          .select('gst_enabled, gst_rate')
          .eq('status', 'active')
          .single();
        
        if (error) {
          console.error('Error fetching organization GST settings:', error);
          return;
        }
        
        if (data) {
          setOrgGstSettings({
            gst_enabled: data.gst_enabled || false,
            gst_rate: parseFloat(data.gst_rate) || 0
          });
        }
      } catch (err) {
        console.error('Error fetching organization GST settings:', err);
      }
    };
    
    fetchOrganizationGstSettings();
  }, []);
  
  // Toggle module expansion
  const toggleModuleExpansion = (moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen: isDocOpen, onOpen: onDocOpen, onClose: onDocClose } = useDisclosure();
  const { isOpen: isInvoiceOpen, onOpen: onInvoiceOpen, onClose: onInvoiceClose } = useDisclosure();
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    onInvoiceOpen();
  };

  const getInvoiceDownloadUrl = (pdfUrl) => {
    const match = pdfUrl?.match(/\/file\/d\/([^/]+)/);
    return match ? `https://drive.google.com/uc?export=download&id=${match[1]}` : pdfUrl;
  };

  console.log("Registration ID from URL:", registrationID);

  // --- Document Template Helper Functions ---
  const formatCurrency = (amount) => {
    const safe = Number.isFinite(Number(amount)) ? Number(amount) : 0;
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 2 }).format(safe);
  };

  const formatInvoiceDate = (date) => {
    if (date == null || date === "") return "-";
    const m = moment(date);
    return m.isValid() ? m.format("DD MMM YYYY") : "-";
  };

  /** Invoice list / calendar: show invoice_type (e.g. registration fee, course fees), not line item text */
  const getInvoiceDescriptionLabel = (invoice) => {
    const raw = invoice?.invoice_type;
    if (raw == null || String(raw).trim() === "") return "-";
    const t = String(raw).trim().toLowerCase();
    if (t === "course") return "Course fees";
    return String(raw).trim();
  };

  /** First token of participant name, title-cased — typical for a friendly but professional greeting */
  const getParticipantGreetingName = (fullName) => {
    if (!fullName || typeof fullName !== "string") return null;
    const t = fullName.trim();
    if (!t) return null;
    const first = t.split(/\s+/)[0];
    if (!first) return null;
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  };

  const getTimeOfDayGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const getDueInBadge = (invoice) => {
    const isPaid = invoice.status === "paid" || invoice.status === "Paid";
    if (isPaid) return null;
    const due = moment(invoice.due_date).startOf("day");
    if (!invoice.due_date || !due.isValid()) return null;
    const today = moment().startOf("day");
    const days = due.diff(today, "days");
    if (days > 1) return { label: `In ${days} days`, bg: "blue.500", color: "white" };
    if (days === 1) return { label: "In 1 day", bg: "blue.500", color: "white" };
    if (days === 0) return { label: "Today", bg: "orange.500", color: "white" };
    if (days === -1) return { label: "1 day overdue", bg: "red.500", color: "white" };
    return { label: `${Math.abs(days)} days overdue`, bg: "red.500", color: "white" };
  };

  const generateInvoiceDueDatesICS = (invoices, certName) => {
    const now = moment().format("YYYYMMDDTHHmmss");
    const courseLabel = certName ? certName.trim() : "Course";
    const events = invoices.map((invoice, index) => {
      const due = moment(invoice.due_date).startOf("day");
      const dateStr = due.format("YYYY-MM-DD");
      const dueDateFormatted = due.format("DD MMM YYYY");
      const subtotal = invoice.invoice_line_items?.length > 0
        ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price || 0) * (item.quantity || 0), 0) : 0;
      const gst = invoice.gst_amount || 0;
      const total = subtotal + gst;
      const amountStr = formatCurrency(total);
      const typeLabel = getInvoiceDescriptionLabel(invoice);
      const descForSummary = invoice.invoice_type ? typeLabel : "Invoice";
      const uid = `invoice-due-${invoice.id}-${index}-${Date.now()}@sozcode.com`;
      const summary = `${courseLabel} - Invoice due: ${descForSummary} (${amountStr})`;
      const descriptionParts = [
        `Course/Program: ${courseLabel}`,
        `Amount due: ${amountStr}`,
        `Due date: ${dueDateFormatted}`,
        invoice.invoice_type ? `Invoice: ${typeLabel}` : null,
        "Payment due by the date above.",
      ].filter(Boolean);
      const description = descriptionParts.join("\\n");
      const dtStart = moment(dateStr).format("YYYYMMDD");
      const dtEnd = moment(dateStr).add(1, "day").format("YYYYMMDD");
      const alarmDesc = `${descForSummary} (${amountStr}) for ${courseLabel} due tomorrow`;
      return ["BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${now}`, `DTSTART;VALUE=DATE:${dtStart}`, `DTEND;VALUE=DATE:${dtEnd}`, `SUMMARY:${summary}`, `DESCRIPTION:${description}`, "STATUS:CONFIRMED", "BEGIN:VALARM", "TRIGGER:-P1D", "ACTION:DISPLAY", `DESCRIPTION:${alarmDesc}`, "END:VALARM", "END:VEVENT"].join("\r\n");
    });
    const calName = certName ? `Invoice due dates - ${certName}` : "Invoice due dates";
    return ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Sozcode LMS//Invoice Due Dates//EN", "CALSCALE:GREGORIAN", "METHOD:PUBLISH", `X-WR-CALNAME:${calName}`, ...events, "END:VCALENDAR"].join("\r\n");
  };

  const handleDownloadInvoiceDueDatesCalendar = (invoices, certName) => {
    const unpaid = invoices.filter((inv) => {
      const paid = inv.status === "paid" || inv.status === "Paid";
      if (paid) return false;
      const due = moment(inv.due_date).startOf("day");
      return inv.due_date && due.isValid();
    });
    if (unpaid.length === 0) {
      toast({ title: "No due dates to add", description: "No unpaid invoices with a due date.", status: "info", duration: 3000, isClosable: true, position: "top" });
      return;
    }
    const name = (certName || "Invoices").replace(/\s+/g, "_");
    downloadICS(generateInvoiceDueDatesICS(unpaid, certName), `Invoice_Due_Dates_${name}.ics`);
    toast({ title: "Calendar file downloaded", description: `${unpaid.length} invoice due date(s) added to the file.`, status: "success", duration: 3000, isClosable: true, position: "top" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB');
  };

  // Remove participant name suffix from document title for display only
  const getDisplayDocumentName = (title) => {
    if (!title) return '';
    // Remove patterns like "- John Doe", "-{{participant_name}}", "-{participant_name}", etc.
    // This removes anything after a dash followed by optional spaces
    return title.replace(/\s*-\s*.*$/, '').trim();
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
    const diffTime = Math.abs(end - start);
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
    return diffMonths.toString();
  };

  const calculateInstallmentSchedule = (reg) => {
    const subtotal = (reg?.full_certification_runs?.full_certification?.cost || 0) - (reg?.full_certification_runs?.full_certification?.discount || 0);
    
    // Apply GST if enabled
    const gstRate = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
    const gstAmount = subtotal * gstRate;
    const totalFees = subtotal + gstAmount;
    
    const numInstallments = parseInt(reg?.full_certification_runs?.full_certification?.installments) || 1;
    const startDate = new Date(reg?.full_certification_runs?.start_date);
    const schedule = [];
    
    // Determine header text based on GST status
    const gstHeaderText = orgGstSettings.gst_enabled 
      ? `Amount (incl. ${orgGstSettings.gst_rate}% GST)<br/>(S$)` 
      : 'Amount<br/>(S$)';
    
    if (!Number.isFinite(totalFees) || Number.isNaN(startDate.getTime()) || numInstallments <= 0) {
      return `<table>
        <tr>
          <th>Instalment Schedule</th>
          <th>${gstHeaderText}</th>
          <th>Date Due</th>
        </tr>
      </table>`;
    }

    const installmentAmount = totalFees / numInstallments;
    let tableRows = '';
    for (let i = 0; i < numInstallments; i++) {
      const installmentDate = new Date(startDate);
      installmentDate.setMonth(installmentDate.getMonth() + i);
      tableRows += `
        <tr>
          <td>${i + 1}</td>
          <td>${formatCurrency(installmentAmount)}</td>
          <td>${formatDate(installmentDate)}</td>
        </tr>`;
    }

    return `<table>
      <tr>
        <th>Instalment Schedule</th>
        <th>${gstHeaderText}</th>
        <th>Date Due</th>
      </tr>
      ${tableRows}
    </table>`;
  };

  // Build HTML by replacing variables in template string
  const buildDocumentHtml = (templateString, variables) => {
    if (!templateString) return '';
    let html = templateString;
    Object.entries(variables).forEach(([key, val]) => {
      const safeVal = val == null ? '' : String(val);
      html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), safeVal);
    });
    return html;
  };

  // Helper to format date for ICS (YYYYMMDD format)
  const formatICSDate = (dateStr) => {
    return moment(dateStr).format("YYYYMMDD");
  };

  // Helper to format datetime for ICS (YYYYMMDDTHHMMSS format)
  const formatICSDateTime = (dateStr, timeStr) => {
    if (!timeStr) return formatICSDate(dateStr);
    const datetime = moment(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm:ss");
    return datetime.format("YYYYMMDDTHHmmss");
  };

  // Generate ICS content for a single session
  const generateICSContent = (session, moduleName, courseTitle) => {
    const uid = `${session.session_id || session.id}-${Date.now()}@sozcode.com`;
    const now = moment().format("YYYYMMDDTHHmmss");
    const courseLabel = courseTitle || moduleName || "Course";
    // Use sessionIndex if available for proper numbering
    const sessionNum = session.sessionIndex ? ` - Session ${session.sessionIndex}` : '';
    const summary = `${moduleName}${sessionNum}`;
    const location = session.is_online ? 'Online' : ([session.venue, session.room].filter(Boolean).join(", ") || "TBC");
    const description = [
      courseTitle ? `Course: ${courseTitle}` : null,
      `Module: ${moduleName}`,
      session.sessionIndex ? `Session: ${session.sessionIndex}` : null,
      session.trainer_name ? `Trainer: ${session.trainer_name}` : null,
      session.venue ? `Venue: ${session.venue}` : null,
      session.room ? `Room: ${session.room}` : null,
      session.venue_details?.address ? `Address: ${session.venue_details.address}` : null,
    ].filter(Boolean).join("\\n");

    let dtStart, dtEnd;
    if (session.start_time && session.end_time) {
      dtStart = formatICSDateTime(session.date, session.start_time);
      dtEnd = formatICSDateTime(session.date, session.end_time);
    } else {
      // All-day event
      dtStart = formatICSDate(session.date);
      dtEnd = formatICSDate(moment(session.date).add(1, 'day').format("YYYY-MM-DD"));
    }

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Sozcode LMS//Course Session//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      session.start_time ? `DTSTART:${dtStart}` : `DTSTART;VALUE=DATE:${dtStart}`,
      session.start_time ? `DTEND:${dtEnd}` : `DTEND;VALUE=DATE:${dtEnd}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      "STATUS:CONFIRMED",
      "BEGIN:VALARM",
      "TRIGGER:-P1D",
      "ACTION:DISPLAY",
      "DESCRIPTION:Reminder: Training session tomorrow",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR"
    ].join("\r\n");

    return icsContent;
  };

  // Generate ICS for multiple sessions (all sessions in a module)
  const generateMultipleICSContent = (sessions, moduleName, courseTitle) => {
    const now = moment().format("YYYYMMDDTHHmmss");
    const courseLabel = courseTitle || moduleName || "Course";
    
    const events = sessions.map((session, index) => {
      const sessionNum = session.sessionIndex || (index + 1);
      const uid = `${session.session_id || session.id}-${index}-${Date.now()}@sozcode.com`;
      const summary = `${moduleName} - Session ${sessionNum}`;
      const location = session.is_online ? 'Online' : ([session.venue, session.room].filter(Boolean).join(", ") || "TBC");
      const description = [
        `Course: ${courseLabel}`,
        `Module: ${moduleName}`,
        `Session: ${sessionNum} of ${sessions.length}`,
        session.trainer_name ? `Trainer: ${session.trainer_name}` : null,
        session.venue ? `Venue: ${session.venue}` : null,
        session.room ? `Room: ${session.room}` : null,
        session.venue_details?.address ? `Address: ${session.venue_details.address}` : null,
      ].filter(Boolean).join("\\n");

      let dtStart, dtEnd;
      if (session.start_time && session.end_time) {
        dtStart = formatICSDateTime(session.date, session.start_time);
        dtEnd = formatICSDateTime(session.date, session.end_time);
      } else {
        dtStart = formatICSDate(session.date);
        dtEnd = formatICSDate(moment(session.date).add(1, 'day').format("YYYY-MM-DD"));
      }

      return [
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTAMP:${now}`,
        session.start_time ? `DTSTART:${dtStart}` : `DTSTART;VALUE=DATE:${dtStart}`,
        session.start_time ? `DTEND:${dtEnd}` : `DTEND;VALUE=DATE:${dtEnd}`,
        `SUMMARY:${summary}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        "STATUS:CONFIRMED",
        "BEGIN:VALARM",
        "TRIGGER:-P1D",
        "ACTION:DISPLAY",
        "DESCRIPTION:Reminder: Training session tomorrow",
        "END:VALARM",
        "END:VEVENT"
      ].join("\r\n");
    });

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Sozcode LMS//Course Sessions//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      `X-WR-CALNAME:${moduleName} - ${courseTitle || moduleName} Sessions`,
      ...events,
      "END:VCALENDAR"
    ].join("\r\n");

    return icsContent;
  };

  // Download ICS file
  const downloadICS = (icsContent, filename) => {
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle single session calendar download
  const handleDownloadSessionCalendar = (session, moduleName, courseTitle) => {
    const icsContent = generateICSContent(session, moduleName, courseTitle);
    const dateStr = moment(session.date).format("YYYYMMDD");
    const filename = `${moduleName.replace(/\s+/g, "_")}_${dateStr}.ics`;
    downloadICS(icsContent, filename);
    toast({
      title: "Calendar Event Downloaded",
      description: "The session has been downloaded as an ICS file. Open it to add to your calendar.",
      status: "success",
      duration: 3000,
    });
  };

  // Handle all sessions calendar download for a module
  const handleDownloadModuleCalendar = (module, courseTitle) => {
    if (!module.sessions || module.sessions.length === 0) {
      toast({
        title: "No Sessions",
        description: "This module has no scheduled sessions yet.",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    const moduleName = module.course_type?.name || module.course_type_code || "Module";
    const icsContent = generateMultipleICSContent(module.sessions, moduleName, courseTitle);
    const filename = `${moduleName.replace(/\s+/g, "_")}_All_Sessions.ics`;
    downloadICS(icsContent, filename);
    toast({
      title: "Calendar Events Downloaded",
      description: `${module.sessions.length} sessions have been downloaded. Open the file to add all to your calendar.`,
      status: "success",
      duration: 4000,
    });
  };

  // Handle download ALL sessions from ALL modules combined
  const handleDownloadAllModulesCalendar = (modules, courseTitle) => {
    const resolvedCourseName = courseTitle || modules[0]?.course_type?.name || modules[0]?.course_type_code || "Course";
    // Collect all sessions from all modules
    const allSessions = [];
    modules.forEach(module => {
      if (module.sessions && module.sessions.length > 0) {
        const moduleName = module.course_type?.name || module.course_type_code || "Module";
        module.sessions.forEach((session, idx) => {
          allSessions.push({
            ...session,
            moduleName: moduleName,
            moduleIndex: modules.indexOf(module) + 1
          });
        });
      }
    });

    if (allSessions.length === 0) {
      toast({
        title: "No Sessions",
        description: "No scheduled sessions found across all modules.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    const now = moment().format("YYYYMMDDTHHmmss");
    
    const events = allSessions.map((session, index) => {
      const sessionNum = session.sessionIndex || (index + 1);
      const uid = `${session.session_id || session.id}-all-${index}-${Date.now()}@sozcode.com`;
      const summary = `${session.moduleName} - Session ${sessionNum}`;
      const location = session.is_online ? 'Online' : ([session.venue, session.room].filter(Boolean).join(", ") || "TBC");
      const description = [
        `Course: ${resolvedCourseName}`,
        `Module: ${session.moduleName}`,
        `Session: ${sessionNum}`,
        session.trainer_name ? `Trainer: ${session.trainer_name}` : null,
        session.is_online ? `Venue: Online - meeting link will be sent a day before` : (session.venue ? `Venue: ${session.venue}` : null),
        session.is_online ? null : (session.room ? `Room: ${session.room}` : null),
        session.is_online ? null : (session.venue_details?.address ? `Address: ${session.venue_details.address}` : null),
      ].filter(Boolean).join("\\n");

      let dtStart, dtEnd;
      if (session.start_time && session.end_time) {
        dtStart = formatICSDateTime(session.date, session.start_time);
        dtEnd = formatICSDateTime(session.date, session.end_time);
      } else {
        dtStart = formatICSDate(session.date);
        dtEnd = formatICSDate(moment(session.date).add(1, 'day').format("YYYY-MM-DD"));
      }

      return [
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTAMP:${now}`,
        session.start_time ? `DTSTART:${dtStart}` : `DTSTART;VALUE=DATE:${dtStart}`,
        session.start_time ? `DTEND:${dtEnd}` : `DTEND;VALUE=DATE:${dtEnd}`,
        `SUMMARY:${summary}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        "STATUS:CONFIRMED",
        "BEGIN:VALARM",
        "TRIGGER:-P1D",
        "ACTION:DISPLAY",
        "DESCRIPTION:Reminder: Training session tomorrow",
        "END:VALARM",
        "END:VEVENT"
      ].join("\r\n");
    });

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Sozcode LMS//All Course Sessions//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      `X-WR-CALNAME:${resolvedCourseName} - All Sessions`,
      ...events,
      "END:VCALENDAR"
    ].join("\r\n");

    const filename = `${resolvedCourseName.replace(/\s+/g, "_")}_All_Sessions.ics`;
    downloadICS(icsContent, filename);
    
    const moduleCount = modules.filter(m => m.sessions?.length > 0).length;
    toast({
      title: "All Calendar Events Downloaded",
      description: `${allSessions.length} sessions from ${moduleCount} modules have been downloaded. Open the file to add all to your calendar.`,
      status: "success",
      duration: 5000,
    });
  };

  // Prepare variables object from registration data
  const prepareDocumentVariables = (raw) => {
    if (!raw) return {};
    
    const courseFees = (raw.full_certification_runs?.full_certification?.cost) || 0;
    const discount = raw.full_certification_runs?.full_certification?.discount || 0;
    const subtotal = courseFees - discount;
    
    // Apply GST if enabled
    const gstRate = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
    const gstAmount = subtotal * gstRate;
    const totalPayable = subtotal + gstAmount;
    
    const numInst = (raw.full_certification_runs?.full_certification?.installment_scheme) ? parseInt(String(raw.full_certification_runs?.full_certification?.installment_scheme).split(' ')[0], 10) : 1;
    const installmentschedule = calculateInstallmentSchedule(raw);

    const calculateFeeBreakdown = (raw) => {
      const courseFees = (raw?.full_certification_runs?.full_certification?.cost) || 0;
      const discount = raw?.full_certification_runs?.full_certification?.discount || 0;
      const subtotal = courseFees - discount;
      
      // Apply GST if enabled
      const gstRate = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
      const gstAmount = subtotal * gstRate;
      const totalPayable = subtotal + gstAmount;
      
      const numInst = parseInt(raw?.full_certification_runs?.full_certification?.installments) || 1;
      
      // Build GST row if enabled
      const gstRow = orgGstSettings.gst_enabled ? `
        <tr>
          <td>GST (${orgGstSettings.gst_rate}%)</td>
          <td>${formatCurrency(gstAmount)}</td>
        </tr>` : '';
      
      return `<table>
        <tr>
          <td>Total Course Fees</td>
          <td>${formatCurrency(courseFees)}</td>
        </tr>
        <tr>
          <td>Medical Insurance</td>
          <td>$0.00</td>
        </tr>
        <tr>
          <td>Fee Protection Scheme Fee</td>
          <td>$0.00</td>
        </tr>
        <tr>
          <td>Discount</td>
          <td>${formatCurrency(discount)}</td>
        </tr>
        <tr>
          <td>Subtotal</td>
          <td>${formatCurrency(subtotal)}</td>
        </tr>${gstRow}
        <tr>
          <td>No of Instalments:</td>
          <td>${numInst}</td>
        </tr>
        <tr>
          <td><strong>Total Course Fees Payable${orgGstSettings.gst_enabled ? ' (incl. GST)' : ''}</strong></td>
          <td><strong>${formatCurrency(totalPayable)}</strong></td>
        </tr>
      </table>`;
    };
    
    const feeBreakdown = calculateFeeBreakdown(raw);

    // Add signature image if available
    const signatureHtml = raw.signature 
      ? `<img src="${raw.signature}" alt="Signature" style="max-width: 200px; height: auto;" />`
      : '';

    return {
      // PEI info (hardcoded)
      date_signed: formatDate(raw.date_signed),
      hr_line: '<hr style="border: 1px solid #000; margin: 20px 0; break-before: page;"/>',
      pei_name: 'EDUSPHERE COLLEGE OF MANAGEMENT AND TECHNOLOGY PTE LTD',
      pei_registration_number: '201940174R',
      pei_address: '7500A Beach Rd, #05-312 THE PLAZA, Singapore 199591',
      pei_signatory_name: 'SAMSUDEEN MOHAMED TAHIR',
      pei_signatory_designation: 'Principal',
      nric: raw.nric || '',
      duration: raw.full_certification_runs?.full_certification?.duration + ' months' || '8 months',
      full_certification_name: raw.name || raw.full_certification_runs?.full_certification?.name || raw.certification_name || 'Course',
      full_certification_title: raw.name || raw.full_certification_runs?.full_certification?.name || raw.certification_name || 'Course',
      course_title: raw.name || raw.full_certification_runs?.full_certification?.name || raw.certification_name || 'Course',
      course_duration_months: calculateDuration(raw.start_date, raw.end_date) || '1',
      course_type: raw.registration_type || '',
      course_commencement_date: formatDate(raw.start_date),
      course_completion_date: formatDate(raw.end_date),
      start_date: formatDate(raw.full_certification_runs?.start_date),
      end_date: formatDate(raw.full_certification_runs?.end_date),
      studies_commencement_date: formatDate(raw.start_date),
      qualification: raw.full_certification_runs?.full_certification?.name || raw.certification_name || raw.name || '',
      // Course requirements (hardcoded)
      course_developer: 'EDUSPHERE COLLEGE OF MANAGEMENT AND TECHNOLOGY PTE LTD',
      awarding_organisation: 'EDUSPHERE COLLEGE OF MANAGEMENT AND TECHNOLOGY PTE LTD',
      course_entry_requirements: "● With minimum 3 GCE 'O' level or International Secondary including English\n● English language proficiency: GCE 'O' level or equivalent\n● Working Experience: Matured candidate (i.e. minimum 30 years old with 8 years of working experience) with another relevant certificate qualification.",
      course_schedule: 'Refer to the attached Time Table',
      scheduled_holidays: 'PH Holidays- Refer to www.mom.gov.sg',
      examination_periods: 'Within 2 months from the each examination',
      exam_results_date: 'Within 2 months from the each examination',
      qualification_conferment_date: 'Within 2 months from the each examination',
      industrial_attachment: 'Within 2 months from the each examination',
      industrial_attachment_duration: 'NA',
      total_course_fees: formatCurrency(courseFees),
      discount: formatCurrency(discount),
      subtotal: formatCurrency(subtotal),
      gst_amount: orgGstSettings.gst_enabled ? formatCurrency(gstAmount) : 'N/A',
      gst_rate: orgGstSettings.gst_enabled ? `${orgGstSettings.gst_rate}%` : 'N/A',
      number_of_instalments: String(numInst),
      total_fees_payable: formatCurrency(totalPayable),
      // Hardcoded Misc Fees
      registration_fee: '$100.00',
      supplementary_exam_fee: '$50.00',
      re_examination_fee: '$100.00',
      re_submission_fee: '$100.00',
      medical_insurance_local: '$80.00',
      medical_insurance: '$0.00',
      fee_protection_scheme: '$0.00',
      late_payment_charges: '$100.00',
      transfer_fee: '$150.00',
      programme_deferment_fee: '$100.00',
      reprint_transcript_fee: '$100.00',
      reprint_certificate_fee: '$150.00',
      instalment_schedule: installmentschedule,
      fee_breakdown: feeBreakdown,
      contract_date: formatDate(new Date()),
      contracting_party_name: raw.participant_name || '',
      contracting_party_id: raw.nric || '',
      guardian_name: '',
      guardian_id: '',
      student_name: raw.participant_name || '',
      participant_name: raw.participant_name || '',
      student_id: raw.nric || '',
      contracting_party_signature_name: raw.participant_name || '',
      contracting_party_signature_id: raw.nric || '',
      acknowledgment_name: raw.participant_name || '',
      witness_name: '',
      witness_id: '',
      student_signature: signatureHtml,
      signature: signatureHtml,
    };
  };

  const getAllDocumentTemplates = async() => {
    const { data: documentTemplates, error: documentTemplatesError } = await supabase
      .from("document_templates")
      .select("*")
    
    if (documentTemplatesError) {
      console.error("Error fetching document templates:", documentTemplatesError);
      throw documentTemplatesError;
    }
    
    console.log("Fetched document templates:", documentTemplates);
    return documentTemplates;
  }

  // Fetch registration data
  const fetchRegistration = async () => {
    setLoading(true);
    try {
      const packageResult = await fetchPublicStudentPackage(supabase, registrationID);
      let data;

      if (!packageResult.error && packageResult.data) {
        const payload = packageResult.data;
        data = {
          ...(payload.form?.values || {}),
          ...(payload.registration || {}),
          ...(payload.student || {}),
          id: payload.id,
          registration_stage: payload.registration_status,
          additional_data: payload.form?.values || {},
          enrollment: payload.enrollment || null,
          payment_status: payload.payment_status || null,
        };

        // Course/certification catalogues are non-sensitive and stay as normal
        // table reads. Only the participant package is protected by the RPC.
        if (data.draft_cert_run_code) {
          const { data: certificationRun, error: certificationRunError } = await supabase
            .from("full_certification_runs")
            .select("*, full_certification(*)")
            .eq("id", data.draft_cert_run_code)
            .single();
          if (!certificationRunError) data.full_certification_runs = certificationRun;
        }
      } else if (isRegistrationPortalRpcUnavailable(packageResult.error)) {
        const legacyResult = await supabase
          .from("registration")
          .select("*, full_certification_runs(*, full_certification(*))")
          .eq("id", registrationID)
          .single();
        if (legacyResult.error) throw legacyResult.error;
        data = legacyResult.data;
      } else {
        throw packageResult.error || new Error('Student package not found');
      }

      // Fetch course run details if available
      let courseRunData = null;
      let courseTypeData = null;
      let modulesData = [];
      let course_dates = [];
      
      if (data.draft_course_run_code) {
        // Fetch course run; get course_type via FK (course_run.course_type_code -> course_type.code)
        const { data: courseRun, error: courseRunError } = await supabase
          .from("course_run")
          .select(`
            *,
            course_type (
              code,
              name,
              short_desc,
              course_duration,
              school,
              price
            ),
            trainers (
              name,
              email
            )
          `)
          .eq("id", data.draft_course_run_code)
          .single();

        if (!courseRunError && courseRun) {
          courseRunData = courseRun;
          console.log("Course run fetched:", courseRun);
          console.log("Nested course_type from FK:", courseRun.course_type);
          
          // course_type from nested select via FK
          courseTypeData = courseRun.course_type ?? null;
          
          // If nested course_type is empty or has no name, fetch from course_type by course_run.course_type_code
          if ((!courseTypeData || !courseTypeData.name) && courseRun.course_type_code) {
            console.log("Nested course_type missing, fetching separately by code:", courseRun.course_type_code);
            const { data: ct, error: ctError } = await supabase
              .from("course_type")
              .select("code, name, short_desc, course_duration, school, price")
              .eq("code", courseRun.course_type_code)
              .single();
            console.log("Fallback course_type fetch result:", ct, "Error:", ctError);
            if (ct) courseTypeData = ct;
          }
          console.log("Final courseTypeData:", courseTypeData);
        } else {
          console.log("Course run fetch failed:", courseRunError);
        }

        // Fetch sessions for the course run
        const { data: sessions, error: sessionsError } = await supabase
          .from("session")
          .select("*")
          .eq("course_run_code", data.draft_course_run_code)
          .order("session_date", { ascending: true });

        if (!sessionsError && sessions && sessions.length > 0) {
          // Resolve venue, room, trainer names like full cert for consistent display
          const cr = courseRunData;
          const venueIds = [...new Set([
            ...sessions.filter(s => s.venue_id).map(s => s.venue_id),
            ...(cr?.venue_id ? [cr.venue_id] : []),
          ])];
          const roomIds = [...new Set([
            ...sessions.filter(s => s.room_id).map(s => s.room_id),
            ...(cr?.room_id ? [cr.room_id] : []),
          ])];
          const trainerIds = [...new Set([
            ...sessions.filter(s => s.trainer_id).map(s => s.trainer_id),
            ...(cr?.trainer_id ? [cr.trainer_id] : []),
          ])];
          const venueMap = {};
          const roomMap = {};
          const trainerMap = {};
          if (venueIds.length > 0) {
            const { data: venues } = await supabase.from('venues').select('code, name, address').in('code', venueIds);
            venues?.forEach(v => { venueMap[v.code] = v; });
          }
          if (roomIds.length > 0) {
            const { data: rooms } = await supabase.from('rooms').select('code, name, floor, unit').in('code', roomIds);
            rooms?.forEach(r => { roomMap[r.code] = r; });
          }
          if (trainerIds.length > 0) {
            const { data: trainers } = await supabase.from('trainers').select('code, name, email').in('code', trainerIds);
            trainers?.forEach(t => { trainerMap[t.code] = t; });
          }
          course_dates = sessions.map((session, idx) => {
            const venueId = session.venue_id || cr?.venue_id;
            const roomId = session.room_id || cr?.room_id;
            const trainerId = session.trainer_id || cr?.trainer_id;
            const venueInfo = venueMap[venueId];
            const roomInfo = roomMap[roomId];
            const trainerInfo = trainerMap[trainerId];
            // For online sessions, show "Online - meeting link will be sent a day before"
            const isOnline = session.is_online === true;
            const venueName = isOnline
              ? 'Online - meeting link will be sent a day before'
              : session.custom_venue || venueInfo?.name || session.venue_id || cr?.venue || 'TBC';
            return {
              id: session.session_id,
              session_id: session.session_id,
              sessionIndex: idx + 1, // session number for ICS
              date: session.session_date,
              start_time: session.start_time,
              end_time: session.end_time,
              room: isOnline ? '-' : (session.custom_room || roomInfo?.name || session.room_id || roomId || 'TBC'),
              room_details: isOnline ? null : roomInfo,
              venue: venueName,
              venue_details: isOnline ? null : venueInfo,
              is_online: isOnline,
              trainer_id: trainerId,
              trainer_name: trainerInfo?.name || trainerId || cr?.instructor_name || 'TBC',
              trainer_details: trainerInfo,
              isCompleted: moment(session.session_date).isBefore(moment(), 'day')
            };
          });
        }
      }

      console.log("Standalone courseTypeData:", courseTypeData);

      // Full-cert modules: prefer registration_modules (confirmed course_run per student), else cert template links
      const certRunCode = data.draft_cert_run_code || data.full_certification_runs?.id;
      let courseRunCodes = [];

      const { data: regModuleRows, error: regModError } = await supabase
        .from("registration_modules")
        .select("course_run_code, created_at")
        .eq("registration_id", registrationID)
        .order("created_at", { ascending: true });

      if (regModError) {
        console.log("registration_modules fetch error:", regModError);
      }
      const seenFromRegistration = new Set();
      for (const row of regModuleRows || []) {
        if (!row.course_run_code || seenFromRegistration.has(row.course_run_code)) continue;
        seenFromRegistration.add(row.course_run_code);
        courseRunCodes.push(row.course_run_code);
      }

      let moduleScheduleSource = courseRunCodes.length > 0 ? "registration_modules" : null;

      if (courseRunCodes.length === 0 && certRunCode) {
        const { data: linkData, error: linkError } = await supabase
          .from("full_cert_many_course_run")
          .select("course_run_code")
          .eq("cert_run_code", certRunCode);

        console.log("Link data from full_cert_many_course_run:", linkData, "Error:", linkError);

        if (!linkError && linkData && linkData.length > 0) {
          const seenLink = new Set();
          for (const l of linkData) {
            if (!l.course_run_code || seenLink.has(l.course_run_code)) continue;
            seenLink.add(l.course_run_code);
            courseRunCodes.push(l.course_run_code);
          }
          moduleScheduleSource = "full_cert_many_course_run";
        }
      }

      console.log(
        "Certification run code:",
        certRunCode,
        "| Module course_run codes (" + (moduleScheduleSource || "none") + "):",
        courseRunCodes
      );

      if (courseRunCodes.length > 0) {
          const runOrder = Object.fromEntries(courseRunCodes.map((id, i) => [id, i]));

          // Fetch course_run details WITH course_type using nested select
          const { data: courseRuns, error: courseRunsError } = await supabase
            .from("course_run")
            .select(`
              id,
              course_type_code,
              start_date,
              end_date,
              venue,
              venue_id,
              room_id,
              trainer_id,
              instructor_name,
              course_type (
                code,
                name,
                short_desc,
                course_duration
              )
            `)
            .in("id", courseRunCodes);

          console.log("Course runs fetched with course_type:", courseRuns, "Error:", courseRunsError);

          if (!courseRunsError && courseRuns && courseRuns.length > 0) {
            courseRuns.sort((a, b) => (runOrder[a.id] ?? 1e9) - (runOrder[b.id] ?? 1e9));

            // Step 4: Fetch sessions for all course runs
            const { data: allSessions, error: sessionsError } = await supabase
              .from("session")
              .select("*")
              .in("course_run_code", courseRunCodes)
              .order("session_date", { ascending: true });

            console.log("Sessions fetched:", allSessions, "Error:", sessionsError);

            const courseRunByCode = Object.fromEntries(courseRuns.map((cr) => [cr.id, cr]));

            // Step 4b: Fetch venue, room, and trainer names (session row and/or parent course_run defaults)
            const venueIds = [...new Set([
              ...(allSessions?.filter(s => s.venue_id).map(s => s.venue_id) || []),
              ...courseRuns.filter((cr) => cr.venue_id).map((cr) => cr.venue_id),
            ])];
            const roomIds = [...new Set([
              ...(allSessions?.filter(s => s.room_id).map(s => s.room_id) || []),
              ...courseRuns.filter((cr) => cr.room_id).map((cr) => cr.room_id),
            ])];
            const trainerIds = [...new Set([
              ...(allSessions?.filter(s => s.trainer_id).map(s => s.trainer_id) || []),
              ...courseRuns.filter((cr) => cr.trainer_id).map((cr) => cr.trainer_id),
            ])];

            // Fetch venue names
            const venueMap = {};
            if (venueIds.length > 0) {
              const { data: venues } = await supabase
                .from("venues")
                .select("code, name, address")
                .in("code", venueIds);
              venues?.forEach(v => { venueMap[v.code] = v; });
            }

            // Fetch room names
            const roomMap = {};
            if (roomIds.length > 0) {
              const { data: rooms } = await supabase
                .from("rooms")
                .select("code, name, floor, unit")
                .in("code", roomIds);
              rooms?.forEach(r => { roomMap[r.code] = r; });
            }

            // Fetch trainer names
            const trainerMap = {};
            if (trainerIds.length > 0) {
              const { data: trainers } = await supabase
                .from("trainers")
                .select("code, name, email")
                .in("code", trainerIds);
              trainers?.forEach(t => { trainerMap[t.code] = t; });
            }

            console.log("Venue map:", venueMap, "Room map:", roomMap, "Trainer map:", trainerMap);

            // Group sessions by course_run_code with resolved names and session index
            const sessionsByRunCode = {};
            const sessionCountByRunCode = {}; // track session number per course run
            if (allSessions) {
              allSessions.forEach(session => {
                if (!sessionsByRunCode[session.course_run_code]) {
                  sessionsByRunCode[session.course_run_code] = [];
                  sessionCountByRunCode[session.course_run_code] = 0;
                }
                sessionCountByRunCode[session.course_run_code]++;
                const sessionIdx = sessionCountByRunCode[session.course_run_code];

                const runMeta = courseRunByCode[session.course_run_code] || {};
                const venueId = session.venue_id || runMeta.venue_id;
                const roomId = session.room_id || runMeta.room_id;
                const trainerId = session.trainer_id || runMeta.trainer_id;
                
                // For online sessions, show "Online - meeting link will be sent a day before"
                const isOnline = session.is_online === true;
                const venueInfo = venueMap[venueId];
                const venueName = isOnline
                  ? 'Online - meeting link will be sent a day before'
                  : session.custom_venue || venueInfo?.name || session.venue_id || runMeta.venue || 'TBC';
                
                // Resolve room name
                const roomInfo = roomMap[roomId];
                const roomName = isOnline ? '-' : (session.custom_room || roomInfo?.name || session.room_id || roomId || 'TBC');
                
                // Resolve trainer name
                const trainerInfo = trainerMap[trainerId];
                const trainerName = trainerInfo?.name || trainerId || runMeta.instructor_name || 'TBC';
                
                sessionsByRunCode[session.course_run_code].push({
                  id: session.session_id,
                  session_id: session.session_id,
                  sessionIndex: sessionIdx, // session number for ICS
                  date: session.session_date,
                  start_time: session.start_time,
                  end_time: session.end_time,
                  room: roomName,
                  room_details: isOnline ? null : roomInfo,
                  venue: venueName,
                  venue_details: isOnline ? null : venueInfo,
                  is_online: isOnline,
                  trainer_id: trainerId,
                  trainer_name: trainerName,
                  trainer_details: trainerInfo,
                  isCompleted: moment(session.session_date).isBefore(moment(), 'day')
                });
              });
            }

            // Step 5: Build the modules data with course_type info (now directly from nested select) and sessions
            modulesData = courseRuns.map((cr) => {
              console.log(`Module ${cr.id}: course_type_code=${cr.course_type_code}, course_type=`, cr.course_type);
              return {
                id: cr.id,
                course_type_code: cr.course_type_code,
                start_date: cr.start_date,
                end_date: cr.end_date,
                venue: cr.venue,
                instructor_name: cr.instructor_name,
                course_type: cr.course_type || null, // Now using the nested course_type directly
                sessions: sessionsByRunCode[cr.id] || []
              };
            });
          
            console.log("Final processed modules data with sessions:", modulesData);
          }
      }

      setRegistrationData({
        ...data,
        course_dates: course_dates,
        course_run: courseRunData,
        course_type: courseTypeData,
        modules: modulesData
      });
      console.log("Registration data:", data);
      console.log("Modules data:", modulesData);
      console.log("Full certification runs ID:", data.full_certification_runs?.id);
    } catch (error) {
      console.error("Error fetching registration:", error);
      toast({
        title: "Error",
        description: "Could not load registration details.",
        status: "error",
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    try {
      const packageResult = await fetchPublicStudentPackage(supabase, registrationID);
      let data;
      if (!packageResult.error && packageResult.data) {
        data = (packageResult.data.invoices || []).map(invoice => ({
          ...invoice,
          invoice_line_items: invoice.line_items || [],
        }));
      } else if (isRegistrationPortalRpcUnavailable(packageResult.error)) {
        const legacyResult = await supabase
          .from("invoices")
          .select("*, invoice_line_items(*)")
          .eq("registration_id", registrationID)
          .order("invoice_date", { ascending: true });
        if (legacyResult.error) throw legacyResult.error;
        data = legacyResult.data;
      } else {
        throw packageResult.error || new Error('Student package not found');
      }
      setInvoices(data || []);
      console.log("Invoices:", data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast({
        title: "Error",
        description: "Could not load invoices.",
        status: "error",
        isClosable: true,
        position: "top",
      });
    }
  };

  // Initialize documents from registration data
  // Show all templates with their status (done, generating, or not started)
  const fetchAndPrepareDocuments = async (showLoading = true) => {
    if (!registrationData) return;

    try {
      const packageResult = await fetchPublicStudentPackage(supabase, registrationID);
      let allTemplates;
      let allDocs;

      if (!packageResult.error && packageResult.data) {
        allTemplates = packageResult.data.document_templates || [];
        allDocs = packageResult.data.documents || [];
      } else if (isRegistrationPortalRpcUnavailable(packageResult.error)) {
        const { data: legacyTemplates, error: templatesError } = await supabase
          .from('google_document_templates')
          .select('id, name, section')
          .eq('is_active', true)
          .eq('section', 'Registration')
          .order('name', { ascending: true });
        if (templatesError) throw templatesError;
        allTemplates = legacyTemplates;

        const { data: legacyDocs, error: docsError } = await supabase
          .from('document_generator_docs')
          .select(`
            id,
            document_name,
            pdf_file_id,
            template_id,
            template:template_id (name)
          `)
          .eq('registration_id', registrationID)
          .order('created_at', { ascending: true });
        if (docsError) throw docsError;
        allDocs = legacyDocs;
      } else {
        throw packageResult.error || new Error('Student package not found');
      }

      if (!allTemplates || allTemplates.length === 0) {
        console.log("No active templates found");
        setDocuments([]);
        setIsPolling(false);
        return { hasDocuments: false, needsGeneration: false, allComplete: true };
      }

      // Filter out invoice templates - they should not be generated here
      const allowedSections = ['Initial Registration', 'Registration', 'Default'];
      const filteredTemplates = allTemplates.filter(template => {
        const section = template.section || 'Default';
        return allowedSections.includes(section);
      });

      if (filteredTemplates.length === 0) {
        console.log("No non-invoice templates found");
        setDocuments([]);
        setIsPolling(false);
        return { hasDocuments: false, needsGeneration: false, allComplete: true };
      }

      // Create a map of template_id to document
      const docsByTemplate = new Map();
      if (allDocs) {
        allDocs.forEach(doc => {
          if (doc.template_id) {
            docsByTemplate.set(doc.template_id, doc);
          }
        });
      }

      // Build document list showing all templates with their status
      const documentsList = filteredTemplates.map(template => {
        const doc = docsByTemplate.get(template.id);
        
        if (doc && doc.pdf_file_id) {
          // Document is done - has PDF
          return {
            id: doc.id,
            templateId: template.id,
            title: doc.document_name || doc.template_name || doc.template?.name || template.name,
            pdfFileId: doc.pdf_file_id,
            pdfUrl: `https://drive.google.com/uc?export=view&id=${doc.pdf_file_id}`, // Direct PDF preview URL
            pdfDownloadUrl: `https://drive.google.com/uc?export=download&id=${doc.pdf_file_id}`,
            isPdf: true,
            status: 'done',
          };
        } else if (doc && !doc.pdf_file_id) {
          // Document is generating - exists but no PDF yet
          return {
            id: doc.id,
            templateId: template.id,
            title: doc.document_name || doc.template_name || doc.template?.name || template.name,
            isGenerating: true,
            isPdf: false,
            status: 'generating',
          };
        } else {
          // Document not started yet - show as generating (will be triggered automatically)
          return {
            id: `template-${template.id}`,
            templateId: template.id,
            title: template.name,
            isGenerating: true, // Show as generating so it displays with loading animation
            isPdf: false,
            status: 'not_started',
          };
        }
      });

      const doneCount = documentsList.filter(d => d.status === 'done').length;
      const notStartedCount = documentsList.filter(d => d.status === 'not_started').length;
      
      setDocuments(documentsList);
      setCurrentDocCount(doneCount);
      
      const allComplete =
        filteredTemplates.length > 0 && doneCount === filteredTemplates.length;

      // Stop polling if all documents are done (same template set as this list)
      if (allComplete) {
        setIsPolling(false);
      } else {
        setIsPolling(true); // Keep polling if some are still generating or not started
      }

      console.log(`Found ${doneCount}/${filteredTemplates.length} completed documents, ${notStartedCount} not started`);
      
      return {
        hasDocuments: doneCount > 0,
        needsGeneration: notStartedCount > 0,
        allComplete,
      };
    } catch (error) {
      console.error("Error preparing documents:", error);
      if (showLoading) {
        toast({
          title: "Error loading documents",
          description: "Could not load documents.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
      setIsPolling(false);
      return null;
    }
  };

  // Trigger PDF generation for all missing templates
  const triggerPDFGenerationForAll = async () => {
    if (!registrationData || !registrationID) return false;

    try {
      const googleUserId = await resolveGoogleDocGenUserId(supabase);

      if (!googleUserId) {
        console.warn('No Google doc-gen account configured. Cannot trigger PDF generation.');
        return false;
      }

      console.log(`Triggering PDF generation for all templates for registration ${registrationID}`);

      // Call copy-google-doc Edge Function - generates all active templates; frontend shows only Registration section
      const { data, error } = await supabase.functions.invoke('copy-google-doc', {
        body: {
          registrationIds: [registrationID],
          userId: googleUserId,
        },
      });

      if (error) {
        console.error('Error triggering PDF generation:', error);
        return false;
      } else {
        console.log('PDF generation job created successfully:', data);
        return true;
      }
    } catch (error) {
      console.error('Error in triggerPDFGenerationForAll:', error);
      return false;
    }
  };

  // Trigger PDF generation if payment received but no job exists
  const triggerPDFGenerationIfNeeded = async () => {
    if (!registrationData || !registrationID) return;

    // Prevent duplicate triggers
    if (hasTriggeredGenerationRef.current) {
      console.log('PDF generation already triggered for this registration, skipping');
      return;
    }

    try {
      // Check if payment has been received
      if (registrationData.registration_stage !== 'payment_received' && registrationData.registration_stage !== 'payment_in_progress') {
        console.log('Payment not received yet, skipping PDF generation trigger');
        return;
      }

      // Check the protected package first. Direct table access remains only as
      // a temporary fallback while the RPC has not yet been deployed.
      const packageResult = await fetchPublicStudentPackage(supabase, registrationID);
      let existingDocs;
      if (!packageResult.error && packageResult.data) {
        existingDocs = (packageResult.data.documents || [])
          .filter(document => document.pdf_file_id)
          .slice(0, 1);
      } else if (isRegistrationPortalRpcUnavailable(packageResult.error)) {
        const legacyResult = await supabase
          .from('document_generator_docs')
          .select('id')
          .eq('registration_id', registrationID)
          .not('pdf_file_id', 'is', null)
          .limit(1);
        existingDocs = legacyResult.data;
      } else {
        console.error('Could not check generated documents:', packageResult.error);
        return;
      }

      // If documents already exist, no need to trigger
      if (existingDocs && existingDocs.length > 0) {
        console.log('Documents already exist for this registration, skipping trigger');
        hasTriggeredGenerationRef.current = true; // Mark as triggered to prevent future checks
        return;
      }

      const googleUserId = await resolveGoogleDocGenUserId(supabase);

      if (!googleUserId) {
        console.warn('No Google doc-gen account configured. Cannot trigger PDF generation.');
        return;
      }

      console.log(`Triggering PDF generation for registration ${registrationID} (payment received but no documents found)`);

      // Call copy-google-doc Edge Function - generates all active templates; frontend shows only Registration section
      const { data, error } = await supabase.functions.invoke('copy-google-doc', {
        body: {
          registrationIds: [registrationID],
          userId: googleUserId,
        },
      });

      // Mark as triggered to prevent duplicate calls
      hasTriggeredGenerationRef.current = true;

      if (error) {
        console.error('Error triggering PDF generation:', error);
        toast({
          title: "PDF Generation Started",
          description: "Documents are being generated in the background. This page will update automatically.",
          status: "info",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      } else {
        console.log('PDF generation job created successfully:', data);
        toast({
          title: "PDF Generation Started",
          description: "Your documents are being generated. This page will update automatically when ready.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      console.error('Error in triggerPDFGenerationIfNeeded:', error);
      // Reset trigger flag on error so it can be retried
      hasTriggeredGenerationRef.current = false;
      // Don't show error toast - just log it, as this is a background operation
    }
  };

  useEffect(() => {
    if (!registrationData || !registrationID) {
      // Clear polling if no registration data
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Initialize: fetch documents first
    const initializeDocuments = async () => {
      const result = await fetchAndPrepareDocuments();

      if (!result) {
        return;
      }

      if (result.allComplete) {
        setIsPolling(false);
        return;
      }

      // If there are templates that need generation, trigger it automatically
      if (result.needsGeneration && !hasTriggeredGenerationRef.current) {
        console.log('Found templates that need generation, triggering automatically...');
        const triggered = await triggerPDFGenerationForAll();
        if (triggered) {
          hasTriggeredGenerationRef.current = true; // Prevent duplicate triggers
          toast({
            title: "Document Generation Started",
            description: "All documents are being generated. This page will update automatically as they complete.",
            status: "info",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          // Small delay then refresh to show generating status
          setTimeout(() => {
            fetchAndPrepareDocuments(false);
          }, 1000);
        }
      }

      setIsPolling(true);

      let pollCount = 0;
      const maxPolls = 60; // Poll for up to 3 minutes (60 * 3 seconds)

      pollIntervalRef.current = setInterval(async () => {
        pollCount++;

        const pollResult = await fetchAndPrepareDocuments(false);

        if (pollResult?.allComplete) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          return;
        }

        if (pollCount >= maxPolls) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          await fetchAndPrepareDocuments(false);
        }
      }, 3000);
    };
    
    initializeDocuments();
    
    // Cleanup polling on unmount or when registration changes
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [registrationData, registrationID]);

  useEffect(() => {
    fetchRegistration();
    fetchInvoices();
    // Reset trigger flag when registration ID changes
    hasTriggeredGenerationRef.current = false;
  }, [registrationID]);
  
  // Also reset trigger flag when registration data changes (e.g., payment received)
  useEffect(() => {
    if (registrationData?.registration_stage === 'payment_received' || registrationData?.registration_stage === 'payment_in_progress') {
      // Reset trigger flag when payment is received to allow auto-generation
      hasTriggeredGenerationRef.current = false;
    }
  }, [registrationData?.registration_stage]);

  // Trigger PDF generation if payment received but no documents exist
  useEffect(() => {
    if (!registrationData || !registrationID) return;

    // Only trigger if payment has been received
    if (registrationData.registration_stage === 'payment_received' || registrationData.registration_stage === 'payment_in_progress') {
      // Small delay to ensure registration data is fully loaded and avoid duplicate triggers
      const timer = setTimeout(() => {
        triggerPDFGenerationIfNeeded();
      }, 1500);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationData?.registration_stage, registrationID]);

  // Helper function to create printable HTML with proper CSS for page breaks
  const createPrintableHTML = (htmlContent, title) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `;
  };

  const viewDocument = (doc) => {
    // Set active document and open modal
    // For PDFs, we'll try iframe first, with option to open in new tab
    setActiveDocument(doc);
    onDocOpen();
  };

  // Download individual document as PDF
  const downloadDocumentAsPdf = async (doc, suppressToast = false) => {
    setDownloadingDoc(doc.title);
    try {
      // If it's a PDF from Google Drive, download directly without opening new tab
      if (doc.isPdf && doc.pdfDownloadUrl) {
        // Create a temporary link element and trigger download
        const link = document.createElement('a');
        link.href = doc.pdfDownloadUrl;
        link.download = `${doc.title}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Mark as downloaded
        setDownloadedDocs(prev => new Set([...prev, doc.id || doc.title]));
        
        if (!suppressToast) {
          toast({
            title: "Download Started",
            description: `Downloading ${doc.title}...`,
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      } else {
        // For HTML documents, use print dialog
        const printableHTML = createPrintableHTML(doc.html, doc.title);
        
        // Create a hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        // Write the HTML to the iframe
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(printableHTML);
        iframeDoc.close();
        
        // Wait for content to load
        await new Promise(resolve => {
          if (iframe.contentWindow) {
            iframe.contentWindow.onload = resolve;
          } else {
            setTimeout(resolve, 500);
          }
        });
        
        // Trigger print dialog
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);

        toast({
          title: "Print Dialog Opened",
          description: `Choose "Save as PDF" to download ${doc.title}.`,
          status: "info",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      console.error("Error downloading document:", error);
      toast({
        title: "Download Error",
        description: "Failed to download document. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Download all completed PDFs by simulating clicks on each download button
  const downloadAllDocumentsAsPdf = async () => {
    setDownloadingAll(true);
    try {
      // Filter only completed PDFs that haven't been downloaded yet
      const completedPdfs = documents.filter(
        doc => doc.status === 'done' && 
        doc.isPdf && 
        doc.pdfDownloadUrl &&
        !downloadedDocs.has(doc.id || doc.title)
      );
      
      if (completedPdfs.length === 0) {
        toast({
          title: "No PDFs Available",
          description: "No new PDFs to download. All PDFs have already been downloaded.",
          status: "info",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setDownloadingAll(false);
        return;
      }

      // Simulate clicking each download button one by one with 2.5 second delays
      for (let i = 0; i < completedPdfs.length; i++) {
        const doc = completedPdfs[i];
        
        // Wait before each download (except the first one)
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 2500)); // 2.5 second delay between downloads
        }
        
        // Simulate clicking the download button by calling the download function
        // Suppress individual toasts during bulk download
        await downloadDocumentAsPdf(doc, true);
      }

      toast({
        title: "All Downloads Started",
        description: `Started downloading ${completedPdfs.length} PDF file(s)...`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      console.error("Error downloading PDFs:", error);
      toast({
        title: "Download Error",
        description: "Failed to download PDFs. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  if (loading) {
    return (
      <Center h="100vh" bg="gray.50">
        <Spinner size="xl" thickness="4px" speed="0.65s" color="brand.600" />
      </Center>
    );
  }

  if (!registrationData) {
    return (
      <Center h="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Text fontSize="lg" fontWeight="semibold">No registration data found.</Text>
          <Button onClick={() => navigate('/')}>Return to Home</Button>
        </VStack>
      </Center>
    );
  }

  const greetingSalutation = getTimeOfDayGreeting();
  const greetingName = getParticipantGreetingName(registrationData.participant_name);

  return (
    <>
      <Container
        w={"100%"}
        maxW="1400px"
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        bg="gray.50"
        minH="100vh"
        py={{ base: 4, md: 8 }}
        px={{ base: 2, md: 4 }}
      >
        <Box
          width="100%"
          bg="white"
          px={{ base: 4, md: 10 }}
          py={{ base: 6, md: 10 }}
          borderRadius="lg"
          boxShadow="md"
        >
          <Flex
            align="flex-start"
            gap={{ base: 4, md: 5 }}
            p={{ base: 4, md: 6 }}
            mb={{ base: 6, md: 8 }}
            borderRadius="2xl"
            border="1px solid"
            borderColor="gray.100"
            borderLeftWidth="4px"
            borderLeftColor="brand.400"
            position="relative"
            overflow="hidden"
            role="region"
            aria-label="Welcome"
            bgGradient="linear(to-br, white, gray.50)"
          >
            <Box
              position="absolute"
              top="-40%"
              right="-8%"
              w={{ base: "140px", md: "180px" }}
              h={{ base: "140px", md: "180px" }}
              borderRadius="full"
              bg="brand.50"
              opacity={0.55}
              pointerEvents="none"
              aria-hidden
            />
            <Flex
              flexShrink={0}
              align="center"
              justify="center"
              w={{ base: "52px", md: "60px" }}
              h={{ base: "52px", md: "60px" }}
              borderRadius="xl"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="sm"
              position="relative"
            >
              <Icon as={FaGraduationCap} boxSize={{ base: 7, md: 8 }} color="brand.500" />
            </Flex>
            <Box flex={1} minW={0} position="relative">
              <Text
                as="p"
                fontSize="xs"
                fontWeight="semibold"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="brand.600"
                mb={{ base: 1, md: 2 }}
              >
                Student package
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="semibold"
                color="gray.800"
                lineHeight="snug"
                letterSpacing="-0.02em"
                mb={2}
              >
                {greetingName ? (
                  <>
                    {greetingSalutation},{" "}
                    <Text as="span" color="brand.700" fontWeight="bold">
                      {greetingName}
                    </Text>
                    .
                  </>
                ) : (
                  <>{greetingSalutation}.</>
                )}
              </Heading>
              <Text fontSize="sm" color="gray.600" lineHeight="tall" maxW="2xl" mt={1}>
                Your course overview, documents, training schedule, and invoices are organised below so you can find what you need quickly.
              </Text>
              <HStack spacing={2} flexWrap="wrap" mt={4} rowGap={2}>
                <Badge
                  variant="subtle"
                  colorScheme="gray"
                  fontSize="xs"
                  px={2.5}
                  py={0.5}
                  borderRadius="md"
                  fontWeight="medium"
                >
                  Overview
                </Badge>
                <Badge variant="subtle" colorScheme="gray" fontSize="xs" px={2.5} py={0.5} borderRadius="md" fontWeight="medium">
                  Documents
                </Badge>
                <Badge variant="subtle" colorScheme="gray" fontSize="xs" px={2.5} py={0.5} borderRadius="md" fontWeight="medium">
                  Schedule
                </Badge>
                <Badge variant="subtle" colorScheme="gray" fontSize="xs" px={2.5} py={0.5} borderRadius="md" fontWeight="medium">
                  Invoices
                </Badge>
              </HStack>
            </Box>
          </Flex>

          {/* Payment Success Alert */}
          {showPaymentSuccess && (
            <Alert 
              status="success" 
              mb={4}
              borderRadius="lg"
              bg="green.50"
              border="1px solid"
              borderColor="green.200"
              flexDirection={{ base: "column", md: "row" }}
            >
              <VStack align="start" spacing={1} flex={1}>
                <HStack>
                  <Icon as={FaCheckCircle} color="green.500" />
                  <Text fontWeight="bold" color="green.700" fontSize={{ base: "sm", md: "md" }}>Payment Received Successfully! Your enrollment is confirmed.</Text>
                </HStack>
                <Text fontSize={{ base: "xs", md: "sm" }} color="green.600">
                  Thank you for your payment. Your enrolllment has been confirmed and your enrollment documents are now being generated.
                </Text>
              </VStack>
    
            </Alert>
          )}

          {/* Course Information Section - Redesigned */}
          <Box mb={8}>
            {/* Header with Progress Overview */}
            <Flex 
              justify="space-between" 
              align={{ base: "flex-start", md: "center" }}
              mb={{ base: 4, md: 6 }}
              flexWrap="wrap"
              gap={4}
              flexDirection={{ base: "column", md: "row" }}
            >
              
              {/* Progress Overview for Full Certification */}
              {registrationData.modules && registrationData.modules.length > 0 && (
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme="green" px={{ base: 2, md: 3 }} py={1} borderRadius="full" fontSize={{ base: "xs", md: "sm" }}>
                    <HStack spacing={1}>
                      <Icon as={FaCheckCircle} boxSize={{ base: 3, md: 4 }} />
                      <Text>{registrationData.modules.filter(m => m.end_date && moment(m.end_date).isBefore(moment(), 'day')).length} Completed</Text>
                    </HStack>
                  </Badge>
                  <Badge colorScheme="blue" px={{ base: 2, md: 3 }} py={1} borderRadius="full" fontSize={{ base: "xs", md: "sm" }}>
                    <HStack spacing={1}>
                      <Icon as={FaHourglassHalf} boxSize={{ base: 3, md: 4 }} />
                      <Text display={{ base: "none", sm: "block" }}>{registrationData.modules.filter(m => m.start_date && moment(m.start_date).isSameOrBefore(moment(), 'day') && m.end_date && moment(m.end_date).isSameOrAfter(moment(), 'day')).length} In Progress</Text>
                      <Text display={{ base: "block", sm: "none" }}>{registrationData.modules.filter(m => m.start_date && moment(m.start_date).isSameOrBefore(moment(), 'day') && m.end_date && moment(m.end_date).isSameOrAfter(moment(), 'day')).length} Progress</Text>
                    </HStack>
                  </Badge>
                  <Badge colorScheme="gray" px={{ base: 2, md: 3 }} py={1} borderRadius="full" fontSize={{ base: "xs", md: "sm" }}>
                    {registrationData.modules.filter(m => !m.start_date || moment(m.start_date).isAfter(moment(), 'day')).length} Upcoming
                  </Badge>
                </HStack>
              )}
            </Flex>
            
            {/* Main Course Header Card */}
            <Box 
              mb={{ base: 4, md: 6 }} 
              p={{ base: 4, md: 6 }} 
              borderRadius="xl" 
              boxShadow="lg"
            >
              <VStack align="start" spacing={3}>
                <Heading size={{ base: "xs", md: "sm" }}>
                  {registrationData.full_certification_runs?.full_certification?.name ||
                   registrationData.course_type?.name ||
                   registrationData.name ||
                   "Course"}
                </Heading>
                {(registrationData.full_certification_runs?.full_certification?.description || 
                  registrationData.course_type?.short_desc) && (
                  <Text fontSize={{ base: "xs", md: "sm" }} opacity={0.9}>
                    {registrationData.full_certification_runs?.full_certification?.description ||
                     registrationData.course_type?.short_desc}
                  </Text>
                )}
                {registrationData.full_certification_runs?.run_name && (
                  <Badge 
                    bg="whiteAlpha.300" 
                    color="white" 
                    px={{ base: 2, md: 3 }} 
                    py={1} 
                    borderRadius="full"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {registrationData.full_certification_runs.run_name}
                  </Badge>
                )}
              </VStack>
            </Box>

            {/* Quick Stats Row */}
            <Box 
              display="grid" 
              gridTemplateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
              gap={{ base: 3, md: 4 }}
              mb={{ base: 4, md: 6 }}
            >
              {/* Start Date */}
              {(registrationData.full_certification_runs?.start_date || 
                registrationData.course_run?.start_date || 
                registrationData.start_date) && (
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="white" 
                  borderRadius="xl" 
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="sm"
                  textAlign="center"
                >
                  <Icon as={FaCalendarAlt} color="blue.500" boxSize={{ base: 4, md: 5 }} mb={2} />
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" textTransform="uppercase" fontWeight="bold">Start Date</Text>
                  <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color="gray.800">
                    {moment(
                      registrationData.full_certification_runs?.start_date || 
                      registrationData.course_run?.start_date || 
                      registrationData.start_date
                    ).format("DD MMM YYYY")}
                  </Text>
                </Box>
              )}

              {/* End Date */}
              {(registrationData.full_certification_runs?.end_date || 
                registrationData.course_run?.end_date || 
                registrationData.end_date) && (
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="white" 
                  borderRadius="xl" 
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="sm"
                  textAlign="center"
                >
                  <Icon as={FaCalendarAlt} color="green.500" boxSize={{ base: 4, md: 5 }} mb={2} />
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" textTransform="uppercase" fontWeight="bold">End Date</Text>
                  <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color="gray.800">
                    {moment(
                      registrationData.full_certification_runs?.end_date || 
                      registrationData.course_run?.end_date || 
                      registrationData.end_date
                    ).format("DD MMM YYYY")}
                  </Text>
                </Box>
              )}

              {/* Duration */}
              {(registrationData.full_certification_runs?.full_certification?.duration || 
                registrationData.course_type?.course_duration) && (
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="white" 
                  borderRadius="xl" 
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="sm"
                  textAlign="center"
                >
                  <Icon as={FaClock} color="purple.500" boxSize={{ base: 4, md: 5 }} mb={2} />
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" textTransform="uppercase" fontWeight="bold">Duration</Text>
                  <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color="gray.800">
                    {registrationData.full_certification_runs?.full_certification?.duration 
                      ? `${registrationData.full_certification_runs.full_certification.duration} Months`
                      : registrationData.course_type?.course_duration || "N/A"}
                  </Text>
                </Box>
              )}

              {/* Total Modules */}
              {registrationData.modules && registrationData.modules.length > 0 && (
                <Box 
                  p={{ base: 3, md: 4 }} 
                  bg="white" 
                  borderRadius="xl" 
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="sm"
                  textAlign="center"
                >
                  <Icon as={FaBook} color="orange.500" boxSize={{ base: 4, md: 5 }} mb={2} />
                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" textTransform="uppercase" fontWeight="bold">Modules</Text>
                  <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color="gray.800">
                    {registrationData.modules.length} Total
                  </Text>
                </Box>
              )}
            </Box>

            {/* Course Schedule Table (for single courses) */}
            {/* Standalone course schedule – same table style as full-cert sessions */}
            {registrationData.course_dates && registrationData.course_dates.length > 0 && !registrationData.modules?.length && (
              <Box 
                mb={{ base: 4, md: 6 }} 
                bg="white" 
                borderRadius="xl" 
                border="1px solid"
                borderColor="gray.100"
                boxShadow="sm"
                overflow="hidden"
              >
                <Box p={{ base: 3, md: 4 }} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} wrap="wrap" gap={3} flexDirection={{ base: "column", sm: "row" }}>
                    <Heading size={{ base: "xs", md: "sm" }} color="brand.700" display="flex" alignItems="center" gap={2}>
                      <Icon as={FaCalendarAlt} />
                      Training Schedule
                    </Heading>
                    <Button
                      size={{ base: "xs", md: "sm" }}
                      colorScheme="purple"
                      leftIcon={<Icon as={FaCalendarPlus} />}
                      onClick={() => handleDownloadAllModulesCalendar(
                        [{ sessions: registrationData.course_dates, course_type: registrationData.course_type, course_type_code: registrationData.course_type?.code }],
                        registrationData.course_type?.name
                      )}
                      w={{ base: "100%", sm: "auto" }}
                    >
                      <Text display={{ base: "none", sm: "block" }}>Add All Sessions to Calendar</Text>
                      <Text display={{ base: "block", sm: "none" }}>Add All to Calendar</Text>
                    </Button>
                  </Flex>
                </Box>
                <Box p={{ base: 3, md: 6 }} pt={{ base: 3, md: 4 }}>
                  <Box overflowX="auto" w="100%">
                    <Table variant="simple" size="sm" bg="white" borderRadius="md">
                      <Thead bg="gray.100">
                        <Tr>
                          <Th width="90px">Status</Th>
                          <Th>Date</Th>
                          <Th>Time</Th>
                          <Th>Venue</Th>
                          <Th>Room</Th>
                          <Th>Trainer</Th>
                          <Th width="50px" textAlign="center">
                            <Icon as={FaCalendarPlus} color="gray.600" />
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {registrationData.course_dates.map((session, sIndex) => {
                          const isSessionCompleted = session.isCompleted;
                          const isSessionToday = moment(session.date).isSame(moment(), 'day');
                          const courseName = registrationData.course_type?.name;
                          return (
                            <Tr 
                              key={session.session_id ?? sIndex} 
                              bg={isSessionCompleted ? "gray.50" : isSessionToday ? "blue.50" : "white"}
                            >
                              <Td>
                                {isSessionCompleted ? (
                                  <Badge colorScheme="gray" size="sm">
                                    <HStack spacing={1}>
                                      <CheckCircleIcon />
                                      <Text>Done</Text>
                                    </HStack>
                                  </Badge>
                                ) : isSessionToday ? (
                                  <Badge colorScheme="blue" size="sm">Today</Badge>
                                ) : (
                                  <Badge colorScheme="green" size="sm">Upcoming</Badge>
                                )}
                              </Td>
                              <Td>
                                <Text fontWeight="medium">{moment(session.date).format("DD MMM YYYY")}</Text>
                                <Text fontSize="xs" color="gray.500">{moment(session.date).format("dddd")}</Text>
                              </Td>
                              <Td>
                                {session.start_time && session.end_time ? (
                                  <Text fontSize="sm">
                                    {moment(session.start_time, "HH:mm:ss").format("h:mm A")} - {moment(session.end_time, "HH:mm:ss").format("h:mm A")}
                                  </Text>
                                ) : (
                                  <Text color="gray.400">TBC</Text>
                                )}
                              </Td>
                              <Td>
                                <Text fontSize="sm" color="gray.700">{session.venue || "-"}</Text>
                                {session.venue_details?.address && (
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>{session.venue_details.address}</Text>
                                )}
                              </Td>
                              <Td>
                                <Text fontSize="sm" color="gray.700">{session.room || "-"}</Text>
                                {session.room_details?.floor != null && (
                                  <Text fontSize="xs" color="gray.500">
                                    Level {session.room_details.floor}
                                    {session.room_details.unit && `, Unit ${session.room_details.unit}`}
                                  </Text>
                                )}
                              </Td>
                              <Td>
                                <HStack spacing={1}>
                                  <Icon as={FaUser} color="gray.400" boxSize={3} />
                                  <Text fontSize="sm" color="gray.700">{session.trainer_name || "-"}</Text>
                                </HStack>
                              </Td>
                              <Td textAlign="center">
                                <Button
                                  size="xs"
                                  colorScheme="purple"
                                  variant="ghost"
                                  title="Add to Calendar"
                                  onClick={() => handleDownloadSessionCalendar(
                                    session,
                                    courseName || "Course",
                                    courseName
                                  )}
                                >
                                  <Icon as={FaCalendarPlus} />
                                </Button>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Modules Section - Expandable Accordion Style */}
            {registrationData.modules && registrationData.modules.length > 0 ? (
              <Box 
                bg="white" 
                borderRadius="xl" 
                border="1px solid"
                borderColor="gray.100"
                boxShadow="sm"
                overflow="hidden"
              >
                <Box p={{ base: 3, md: 4 }} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} wrap="wrap" gap={3} flexDirection={{ base: "column", sm: "row" }}>
                    <HStack>
                      <Heading size={{ base: "xs", md: "sm" }} color="brand.700" display="flex" alignItems="center" gap={2}>
                        <Icon as={FaBook} />
                        Course Modules ({registrationData.modules.length})
                      </Heading>
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" display={{ base: "none", md: "block" }}>
                        - Click to expand
                      </Text>
                    </HStack>
                    <Button
                      size={{ base: "xs", md: "sm" }}
                      colorScheme="teal"
                      variant="outline"
                      leftIcon={<Icon as={FaCalendarPlus} />}
                      onClick={() => handleDownloadAllModulesCalendar(
                        registrationData.modules, 
                        registrationData.full_certification_runs?.full_certification?.name || registrationData.course_type?.name
                      )}
                      w={{ base: "100%", sm: "auto" }}
                      sx={{ fontWeight: 500, letterSpacing: "normal" }}
                    >
                      <Box as="span" display={{ base: "none", sm: "inline" }}>Add All Sessions to Calendar</Box>
                      <Box as="span" display={{ base: "inline", sm: "none" }}>Add All to Calendar</Box>
                    </Button>
                  </Flex>
                </Box>
                
                <VStack spacing={0} align="stretch" divider={<Divider />}>
                  {registrationData.modules.map((module, index) => {
                    const isModuleCompleted = module.end_date && moment(module.end_date).isBefore(moment(), 'day');
                    const isModuleInProgress = module.start_date && 
                      moment(module.start_date).isSameOrBefore(moment(), 'day') && 
                      module.end_date && 
                      moment(module.end_date).isSameOrAfter(moment(), 'day');
                    const moduleName = module.course_type?.name || module.course_type_code || `Module ${index + 1}`;
                    const isExpanded = expandedModules.has(module.id);
                    const completedSessions = module.sessions?.filter(s => s.isCompleted).length || 0;
                    const totalSessions = module.sessions?.length || 0;
                    
                    return (
                      <Box key={module.id || index}>
                        {/* Module Header Row - Clickable */}
                        <Box
                          p={{ base: 3, md: 4 }}
                          cursor="pointer"
                          onClick={() => toggleModuleExpansion(module.id)}
                          bg={isModuleCompleted ? "green.50" : isModuleInProgress ? "blue.50" : "white"}
                          _hover={{ bg: isModuleCompleted ? "green.100" : isModuleInProgress ? "blue.100" : "gray.50" }}
                          transition="all 0.2s"
                          borderLeft="4px solid"
                          borderColor={isModuleCompleted ? "green.400" : isModuleInProgress ? "blue.400" : "gray.200"}
                        >
                          <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} flexDirection={{ base: "column", md: "row" }} gap={{ base: 2, md: 0 }}>
                            <HStack spacing={{ base: 2, md: 4 }} flex={1} w="100%">
                              {/* Module Number */}
                              <Box
                                w={{ base: 6, md: 8 }}
                                h={{ base: 6, md: 8 }}
                                borderRadius="full"
                                bg={isModuleCompleted ? "green.500" : isModuleInProgress ? "blue.500" : "gray.300"}
                                color="white"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontWeight="bold"
                                fontSize={{ base: "xs", md: "sm" }}
                                flexShrink={0}
                              >
                                {isModuleCompleted ? <CheckCircleIcon boxSize={{ base: 3, md: 4 }} /> : index + 1}
                              </Box>
                              
                              {/* Module Info */}
                              <VStack align="start" spacing={0} flex={1} minW={0}>
                                <Text fontWeight="semibold" color="gray.800" fontSize={{ base: "sm", md: "md" }} noOfLines={2}>
                                  {moduleName}
                                </Text>
                                <HStack spacing={{ base: 2, md: 4 }} mt={1} flexWrap="wrap">
                                  <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                                    <Icon as={FaCalendarAlt} mr={1} boxSize={{ base: 2, md: 3 }} />
                                    {module.start_date ? moment(module.start_date).format("DD MMM") : "TBC"} - {module.end_date ? moment(module.end_date).format("DD MMM YYYY") : "TBC"}
                                  </Text>
                                  {totalSessions > 0 && (
                                    <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                                      <Icon as={FaChalkboardTeacher} mr={1} boxSize={{ base: 2, md: 3 }} />
                                      {completedSessions}/{totalSessions} sessions
                                    </Text>
                                  )}
                                </HStack>
                              </VStack>
                            </HStack>
                            
                            {/* Status Badge and Expand Icon */}
                            <HStack spacing={2} alignSelf={{ base: "flex-end", md: "auto" }} mt={{ base: 2, md: 0 }}>
                              <Badge 
                                colorScheme={isModuleCompleted ? "green" : isModuleInProgress ? "blue" : "gray"}
                                px={{ base: 2, md: 3 }}
                                py={1}
                                borderRadius="full"
                                fontSize={{ base: "xs", md: "sm" }}
                              >
                                {isModuleCompleted ? "Completed" : isModuleInProgress ? "In Progress" : "Upcoming"}
                              </Badge>
                              <Icon 
                                as={isExpanded ? ChevronUpIcon : ChevronDownIcon} 
                                color="gray.500"
                                boxSize={{ base: 4, md: 5 }}
                              />
                            </HStack>
                          </Flex>
                        </Box>
                        
                        {/* Expanded Sessions Content */}
                        {isExpanded && (
                          <Box 
                            bg="gray.50" 
                            px={{ base: 3, md: 6 }} 
                            py={{ base: 3, md: 4 }}
                            borderTop="1px solid"
                            borderColor="gray.200"
                          >
                            {module.sessions && module.sessions.length > 0 ? (
                              <VStack spacing={3} align="stretch">
                                {/* Add All to Calendar Button */}
                                <Flex justify="flex-end">
                                  <Button
                                    size={{ base: "xs", md: "sm" }}
                                    colorScheme="purple"
                                    variant="outline"
                                    leftIcon={<Icon as={FaCalendarPlus} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadModuleCalendar(module, registrationData.full_certification_runs?.full_certification?.name || registrationData.course_type?.name);
                                    }}
                                    w={{ base: "100%", sm: "auto" }}
                                  >
                                    <Text display={{ base: "none", sm: "block" }}>Add All {module.sessions.length} Sessions to Calendar</Text>
                                    <Text display={{ base: "block", sm: "none" }}>Add All to Calendar</Text>
                                  </Button>
                                </Flex>
                                <Box overflowX="auto" w="100%">
                                  <Table variant="simple" size="sm" bg="white" borderRadius="md">
                                    <Thead bg="gray.100">
                                      <Tr>
                                        <Th width="90px">Status</Th>
                                        <Th>Date</Th>
                                        <Th>Time</Th>
                                        <Th>Venue</Th>
                                        <Th>Room</Th>
                                        <Th>Trainer</Th>
                                        <Th width="50px" textAlign="center">
                                          <Icon as={FaCalendarPlus} color="gray.600" />
                                        </Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {module.sessions.map((session, sIndex) => {
                                        const isSessionCompleted = session.isCompleted;
                                        const isSessionToday = moment(session.date).isSame(moment(), 'day');
                                        return (
                                          <Tr 
                                            key={sIndex}
                                            bg={isSessionCompleted ? "gray.50" : isSessionToday ? "blue.50" : "white"}
                                          >
                                            <Td>
                                              {isSessionCompleted ? (
                                                <Badge colorScheme="gray" size="sm">
                                                  <HStack spacing={1}>
                                                    <CheckCircleIcon />
                                                    <Text>Done</Text>
                                                  </HStack>
                                                </Badge>
                                              ) : isSessionToday ? (
                                                <Badge colorScheme="blue" size="sm">Today</Badge>
                                              ) : (
                                                <Badge colorScheme="green" size="sm">Upcoming</Badge>
                                              )}
                                            </Td>
                                            <Td>
                                              <Text fontWeight="medium">
                                                {moment(session.date).format("DD MMM YYYY")}
                                              </Text>
                                              <Text fontSize="xs" color="gray.500">
                                                {moment(session.date).format("dddd")}
                                              </Text>
                                            </Td>
                                            <Td>
                                              {session.start_time && session.end_time ? (
                                                <Text fontSize="sm">
                                                  {moment(session.start_time, "HH:mm:ss").format("h:mm A")} - {moment(session.end_time, "HH:mm:ss").format("h:mm A")}
                                                </Text>
                                              ) : (
                                                <Text color="gray.400">TBC</Text>
                                              )}
                                            </Td>
                                            <Td>
                                              <Text fontSize="sm" color="gray.700">{session.venue || "-"}</Text>
                                              {session.venue_details?.address && (
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                  {session.venue_details.address}
                                                </Text>
                                              )}
                                            </Td>
                                            <Td>
                                              <Text fontSize="sm" color="gray.700">{session.room || "-"}</Text>
                                              {session.room_details?.floor && (
                                                <Text fontSize="xs" color="gray.500">
                                                  Level {session.room_details.floor}
                                                  {session.room_details.unit && `, Unit ${session.room_details.unit}`}
                                                </Text>
                                              )}
                                            </Td>
                                            <Td>
                                              <HStack spacing={1}>
                                                <Icon as={FaUser} color="gray.400" boxSize={3} />
                                                <Text fontSize="sm" color="gray.700">{session.trainer_name || "-"}</Text>
                                              </HStack>
                                            </Td>
                                            <Td textAlign="center">
                                              <Button
                                                size="xs"
                                                colorScheme="purple"
                                                variant="ghost"
                                                title="Add to Calendar"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDownloadSessionCalendar(
                                                    session, 
                                                    moduleName, 
                                                    registrationData.full_certification_runs?.full_certification?.name || registrationData.course_type?.name
                                                  );
                                                }}
                                              >
                                                <Icon as={FaCalendarPlus} />
                                              </Button>
                                            </Td>
                                          </Tr>
                                        );
                                      })}
                                    </Tbody>
                                  </Table>
                                </Box>
                              </VStack>
                            ) : (
                              <Box textAlign="center" py={4}>
                                <Text color="gray.500" fontSize="sm">
                                  <Icon as={FaInfoCircle} mr={2} />
                                  Session schedule will be available soon.
                                </Text>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            ) : registrationData.draft_cert_run_code && (
              <Box p={4} bg="yellow.50" borderRadius="lg" border="1px solid" borderColor="yellow.200">
                <HStack spacing={2}>
                  <Icon as={FaInfoCircle} color="yellow.600" />
                  <Text color="yellow.800" fontSize="sm">
                    Module schedule will be available soon. Please check back later.
                  </Text>
                </HStack>
              </Box>
            )}
          </Box>

          {/* Invoices Section */}
          {invoices.length > 0 && (() => {
            const unpaidWithDueDate = invoices.filter((inv) => {
              const paid = inv.status === "paid" || inv.status === "Paid";
              if (paid) return false;
              const due = moment(inv.due_date).startOf("day");
              return inv.due_date && due.isValid();
            });
            const certName = registrationData?.full_certification_runs?.full_certification?.name || registrationData?.name || "Course";
            return (
            <Box mb={{ base: 6, md: 8 }} p={{ base: 3, md: 6 }} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
              <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={{ base: 3, md: 4 }} flexDirection={{ base: "column", md: "row" }} gap={3}>
                <Heading size={{ base: "xs", md: "sm" }} color="brand.700">
                  Invoices
                </Heading>
                {unpaidWithDueDate.length > 0 && (
                  <Button
                    size={{ base: "xs", md: "sm" }}
                    colorScheme="purple"
                    variant="outline"
                    leftIcon={<Icon as={FaCalendarPlus} />}
                    onClick={() => handleDownloadInvoiceDueDatesCalendar(invoices, certName)}
                    w={{ base: "100%", md: "auto" }}
                  >
                    Add due dates to calendar
                  </Button>
                )}
              </Flex>
              
              {/* Mobile Card View */}
              <VStack spacing={2} align="stretch" display={{ base: "flex", md: "none" }}>
                {invoices.map((invoice) => {
                  const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                  const subtotalAmount = invoice.invoice_line_items && invoice.invoice_line_items.length > 0
                    ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0)
                    : 0;
                  // Use stored gst_amount; fall back to computing from orgGstSettings for legacy invoices
                  const effectiveGstRate = invoice.gst_rate != null ? invoice.gst_rate : (orgGstSettings.gst_enabled ? orgGstSettings.gst_rate : 0);
                  const gstAmount = invoice.gst_amount != null
                    ? invoice.gst_amount
                    : Math.round(subtotalAmount * (effectiveGstRate / 100) * 100) / 100;
                  const totalAmount = subtotalAmount + gstAmount;
                  const dueInBadge = getDueInBadge(invoice);
                  return (
                    <Box
                      key={invoice.id}
                      bg="white"
                      p={3}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={isPaid ? "green.200" : "gray.200"}
                      borderLeft={isPaid ? "4px solid" : "1px solid"}
                      borderLeftColor={isPaid ? "green.400" : "gray.200"}
                      boxShadow="sm"
                      cursor={invoice.pdf_url ? "pointer" : "default"}
                      onClick={() => invoice.pdf_url && handleViewInvoice(invoice)}
                      _hover={invoice.pdf_url ? { boxShadow: "md", borderColor: isPaid ? "green.300" : "brand.300" } : {}}
                      transition="all 0.15s"
                    >
                      <Flex justify="space-between" align="center" mb={2}>
                        <HStack spacing={2} flex={1}>
                          <Text fontWeight="bold" fontSize="sm" color="gray.800">
                            {invoice.invoice_type}
                          </Text>
                          <Badge colorScheme={isPaid ? 'green' : 'orange'} fontSize="2xs" px={1.5} py={0.5}>
                            {invoice.status}
                          </Badge>
                        </HStack>
                        <Text fontWeight="bold" fontSize="md" color={isPaid ? "green.600" : "gray.800"}>
                          {formatCurrency(totalAmount)}
                        </Text>
                      </Flex>
                      
                      <VStack align="stretch" spacing={1.5}>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium">Invoice Date</Text>
                          <Text fontSize="xs" color="gray.700" fontWeight={isPaid ? "semibold" : "normal"}>
                            {formatInvoiceDate(invoice.invoice_date)}
                          </Text>
                        </Flex>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium">Due Date</Text>
                          <Text fontSize="xs" color="gray.700" fontWeight={isPaid ? "semibold" : "normal"}>
                            {formatInvoiceDate(invoice.due_date)}
                          </Text>
                        </Flex>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium">Due in</Text>
                          {dueInBadge ? <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="2xs" px={2} py={0.5}>{dueInBadge.label}</Badge> : <Text fontSize="xs" color="gray.500">—</Text>}
                        </Flex>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium">Subtotal</Text>
                          <Text fontSize="xs" color="gray.700" fontWeight={isPaid ? "semibold" : "normal"}>
                            {formatCurrency(subtotalAmount)}
                          </Text>
                        </Flex>
                        {gstAmount > 0 && (
                          <Flex justify="space-between" align="center">
                            <Text fontSize="2xs" color="gray.500" fontWeight="medium">GST</Text>
                            <Text fontSize="xs" color="gray.700" fontWeight={isPaid ? "semibold" : "normal"}>
                              {formatCurrency(gstAmount)}
                            </Text>
                          </Flex>
                        )}
                        <Box>
                          <Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5}>Description</Text>
                          <Text fontSize="xs" color="gray.700" noOfLines={2}>
                            {getInvoiceDescriptionLabel(invoice)}
                          </Text>
                        </Box>
                        {invoice.pdf_url && (
                          <Flex justify="flex-end" pt={1}>
                            <HStack spacing={1}>
                              <IconButton
                                icon={<ViewIcon />}
                                size="xs"
                                variant="ghost"
                                colorScheme="brand"
                                aria-label="View PDF"
                                onClick={(e) => { e.stopPropagation(); handleViewInvoice(invoice); }}
                              />
                              <IconButton
                                as="a"
                                href={getInvoiceDownloadUrl(invoice.pdf_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<DownloadIcon />}
                                size="xs"
                                variant="ghost"
                                colorScheme="teal"
                                aria-label="Download PDF"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </HStack>
                          </Flex>
                        )}
                      </VStack>
                    </Box>
                  );
                })}
              </VStack>

              {/* Desktop Table View */}
              <Box overflowX="auto" w="100%" display={{ base: "none", md: "block" }}>
                <Table variant="simple" size="sm" bg="white">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Invoice Date</Th>
                    <Th>Due Date</Th>
                    <Th>Due in</Th>
                    <Th>Description</Th>
                    <Th isNumeric>Subtotal</Th>
                    <Th isNumeric>GST</Th>
                    <Th isNumeric>Total</Th>
                    <Th>Status</Th>
                    <Th>PDF</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {invoices.map((invoice) => {
                    const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                    const subtotalAmount = invoice.invoice_line_items && invoice.invoice_line_items.length > 0
                      ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0)
                      : 0;
                    // Use stored gst_amount; fall back to computing from orgGstSettings for legacy invoices
                    const effectiveGstRate = invoice.gst_rate != null ? invoice.gst_rate : (orgGstSettings.gst_enabled ? orgGstSettings.gst_rate : 0);
                    const gstAmount = invoice.gst_amount != null
                      ? invoice.gst_amount
                      : Math.round(subtotalAmount * (effectiveGstRate / 100) * 100) / 100;
                    const totalAmount = subtotalAmount + gstAmount;
                    const dueInBadge = getDueInBadge(invoice);
                    return (
                      <Tr 
                        key={invoice.id}
                        bg={isPaid ? "green.50" : "white"}
                        _hover={{ bg: isPaid ? "green.100" : "gray.50", cursor: invoice.pdf_url ? "pointer" : "default" }}
                        borderLeft={isPaid ? "4px solid" : "none"}
                        borderColor={isPaid ? "green.400" : "transparent"}
                        onClick={() => invoice.pdf_url && handleViewInvoice(invoice)}
                        transition="all 0.15s"
                      >
                        <Td fontWeight={isPaid ? "semibold" : "normal"}>
                          {formatInvoiceDate(invoice.invoice_date)}
                        </Td>
                        <Td fontWeight={isPaid ? "semibold" : "normal"}>
                          {formatInvoiceDate(invoice.due_date)}
                        </Td>
                        <Td>{dueInBadge ? <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="xs" px={2} py={1}>{dueInBadge.label}</Badge> : <Text as="span" color="gray.400">—</Text>}</Td>
                        <Td fontWeight={isPaid ? "semibold" : "normal"}>
                          {getInvoiceDescriptionLabel(invoice)}
                        </Td>
                        <Td isNumeric fontWeight={isPaid ? "semibold" : "normal"}>
                          {formatCurrency(subtotalAmount)}
                        </Td>
                        <Td isNumeric fontWeight={isPaid ? "semibold" : "normal"} color={gstAmount > 0 ? "gray.700" : "gray.400"}>
                          {gstAmount > 0 ? formatCurrency(gstAmount) : "-"}
                        </Td>
                        <Td isNumeric fontWeight="bold" color={isPaid ? "green.600" : "gray.800"}>
                          {formatCurrency(totalAmount)}
                        </Td>
                        <Td>
                          <Badge colorScheme={isPaid ? 'green' : 'orange'} fontSize="sm" px={2} py={1}>
                            {invoice.status}
                          </Badge>
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          {invoice.pdf_url ? (
                            <HStack spacing={1}>
                              <IconButton
                                icon={<ViewIcon />}
                                size="xs"
                                variant="ghost"
                                colorScheme="brand"
                                aria-label="View invoice PDF"
                                title="View PDF"
                                onClick={() => handleViewInvoice(invoice)}
                              />
                              <IconButton
                                as="a"
                                href={getInvoiceDownloadUrl(invoice.pdf_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<DownloadIcon />}
                                size="xs"
                                variant="ghost"
                                colorScheme="teal"
                                aria-label="Download invoice PDF"
                                title="Download PDF"
                              />
                            </HStack>
                          ) : (
                            <Text fontSize="xs" color="gray.400">Generating…</Text>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
              </Box>
            </Box>
            );
          })()}

          {/* Documents Section */}
          <Box mb={{ base: 6, md: 8 }}>
            <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={4} flexDirection={{ base: "column", md: "row" }} gap={3}>
              <Heading size={{ base: "xs", md: "sm" }} color="brand.700">
                Enrollment Documents
              </Heading>
              <HStack spacing={2} flexWrap="wrap" w={{ base: "100%", md: "auto" }}>
                {isPolling && documents.length > 0 && (
                  <Alert status="info" size="sm" borderRadius="md" px={3} py={2} w={{ base: "100%", md: "auto" }}>
                    <Spinner size="xs" mr={2} />
                    <Text fontSize={{ base: "2xs", md: "xs" }}>
                      Generating documents... {currentDocCount}/{documents.length} ready
                    </Text>
                  </Alert>
                )}
                {(() => {
                  const undownloadedPdfs = documents.filter(
                    d => d.status === 'done' && d.isPdf && !downloadedDocs.has(d.id || d.title)
                  );
                  return undownloadedPdfs.length > 0 && (
                    <Button
                      leftIcon={<DownloadIcon />}
                      colorScheme="teal"
                      size={{ base: "xs", md: "sm" }}
                      onClick={downloadAllDocumentsAsPdf}
                      isLoading={downloadingAll}
                      loadingText="Downloading..."
                      w={{ base: "100%", md: "auto" }}
                      sx={{ fontWeight: 500, letterSpacing: "normal" }}
                    >
                      <Box as="span" display={{ base: "none", sm: "inline" }}>Download All PDFs ({undownloadedPdfs.length})</Box>
                      <Box as="span" display={{ base: "inline", sm: "none" }}>Download All ({undownloadedPdfs.length})</Box>
                    </Button>
                  );
                })()}
              </HStack>
            </Flex>

            {documents.length > 0 ? (
              <VStack spacing={3} align="stretch">
                {documents.map((doc, index) => {
                  const isDone = doc.status === 'done';
                  const isGenerating = doc.status === 'generating';
                  const isNotStarted = doc.status === 'not_started';

                  return (
                    <Flex
                      key={doc.id || index}
                      align={{ base: "flex-start", md: "center" }}
                      p={{ base: 3, md: 4 }}
                      border="1px solid"
                      borderColor={
                        isDone ? "green.200" : 
                        (isGenerating || isNotStarted) ? "yellow.200" : 
                        "gray.200"
                      }
                      borderRadius="md"
                      bg={
                        isDone ? "white" : 
                        (isGenerating || isNotStarted) ? "yellow.50" : 
                        "gray.50"
                      }
                      justify="space-between"
                      _hover={isDone ? { bg: "gray.50", borderColor: "brand.400" } : {}}
                      transition="all 0.2s"
                      flexDirection={{ base: "column", md: "row" }}
                      gap={{ base: 3, md: 0 }}
                    >
                      <HStack spacing={3} flex="1" mr={{ base: 0, md: 4 }} w={{ base: "100%", md: "auto" }}>
                        {isDone ? (
                          <Box color="green.500" fontSize={{ base: "20px", md: "24px" }}>
                            <CheckCircleIcon />
                          </Box>
                        ) : (isGenerating || isNotStarted) ? (
                          <Spinner size="sm" color="yellow.500" />
                        ) : (
                          <Box color="gray.400" fontSize={{ base: "20px", md: "24px" }}>
                            <FaFileAlt />
                          </Box>
                        )}
                        <VStack align="start" spacing={0} flex={1} minW={0}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Text fontWeight="semibold" fontSize={{ base: "sm", md: "md" }} color={isDone ? "gray.900" : (isGenerating || isNotStarted) ? "gray.700" : "gray.500"} noOfLines={2}>
                              {getDisplayDocumentName(doc.title)}
                            </Text>
                            {isDone && (
                              <Badge colorScheme="green" size={{ base: "xs", md: "sm" }}>Done</Badge>
                            )}
                            {(isGenerating || isNotStarted) && (
                              <Badge colorScheme="yellow" size={{ base: "xs", md: "sm" }}>Generating</Badge>
                            )}
                          </HStack>
                          <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                            {isDone 
                              ? 'PDF Document ready' 
                              : (isGenerating || isNotStarted)
                              ? 'Please wait while this document is being generated...'
                              : 'Document generation has not started yet'}
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack spacing={2} w={{ base: "100%", md: "auto" }} justify={{ base: "flex-end", md: "auto" }}>
                        <Button
                          size={{ base: "xs", md: "sm" }}
                          variant="outline"
                          leftIcon={<ViewIcon />}
                          onClick={() => viewDocument(doc)}
                          isDisabled={!isDone}
                          flex={{ base: 1, md: "none" }}
                          sx={{ fontWeight: 500, letterSpacing: "normal" }}
                        >
                          View
                        </Button>
                        <Button
                          size={{ base: "xs", md: "sm" }}
                          colorScheme={downloadedDocs.has(doc.id || doc.title) ? "green" : "teal"}
                          leftIcon={downloadedDocs.has(doc.id || doc.title) ? <CheckCircleIcon /> : <DownloadIcon />}
                          onClick={() => downloadDocumentAsPdf(doc)}
                          isLoading={downloadingDoc === doc.title}
                          loadingText="Downloading..."
                          isDisabled={!isDone}
                          flex={{ base: 1, md: "none" }}
                          sx={{ fontWeight: 500, letterSpacing: "normal" }}
                        >
                          <Box as="span" display={{ base: "none", sm: "inline" }}>{downloadedDocs.has(doc.id || doc.title) ? "Downloaded" : "Download PDF"}</Box>
                          <Box as="span" display={{ base: "inline", sm: "none" }}>{downloadedDocs.has(doc.id || doc.title) ? "Done" : "Download"}</Box>
                        </Button>
                      </HStack>
                    </Flex>
                  );
                })}
              </VStack>
            ) : (
              <Box p={6} bg="gray.50" borderRadius="md" textAlign="center">
                <Text color="gray.600">No documents available at this time.</Text>
              </Box>
            )}
          </Box>

          {/* Sign Documents – only show for full certification courses (not standalone) */}
          {registrationData?.full_certification_runs && (
            <Box mt={{ base: 6, md: 8 }} mb={{ base: 2, md: 4 }} textAlign="center" w="100%">
              <Button
                size="lg"
                colorScheme="brand"
                leftIcon={<Icon as={FaSignature} />}
                onClick={() => navigate(`/enrollment/${registrationID}`)}
                px={8}
                py={6}
                fontSize="md"
              >
                Sign Documents
              </Button>
              <Text mt={2} fontSize="sm" color="gray.600">
                Complete your signature on the enrollment page
              </Text>
            </Box>
          )}
        </Box>
      </Container>

      {/* Invoice PDF Viewer Modal */}
      <InvoicePdfModal
        invoice={selectedInvoice}
        isOpen={isInvoiceOpen}
        onClose={onInvoiceClose}
      />

      {/* Document Viewer Modal - Improved desktop layout */}
      <Modal 
        isOpen={isDocOpen} 
        onClose={onDocClose} 
        size={{ base: "full", md: "6xl" }} 
        scrollBehavior="inside"
        isCentered={{ base: false, md: true }}
      >
        <ModalOverlay />
        <ModalContent 
          maxW={{ base: "100vw", md: "90vw" }} 
          h={{ base: "100vh", md: "90vh" }} 
          maxH={{ base: "100vh", md: "90vh" }} 
          m={{ base: 0, md: "auto" }} 
          borderRadius={{ base: 0, md: "xl" }} 
          display="flex" 
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader color="brand.700" borderBottom="2px solid" borderColor="brand.300" fontSize={{ base: "sm", md: "md" }} py={{ base: 2, md: 4 }} px={{ base: 3, md: 6 }} flexShrink={0}>
            {getDisplayDocumentName(activeDocument?.title) || "Document"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0} flex="1" overflow="hidden" display="flex" flexDirection="column" bg="gray.100">
            {activeDocument && activeDocument.isPdf && activeDocument.pdfFileId ? (
              <Box
                p={0}
                flex="1"
                display="flex"
                flexDirection="column"
                minH={0}
                w="full"
              >
                {/* Display PDF using Google Drive viewer in iframe - file is public so should work */}
                <iframe
                  src={`https://drive.google.com/file/d/${activeDocument.pdfFileId}/preview`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    flex: 1,
                  }}
                  title={activeDocument.title}
                />
              </Box>
            ) : (
              <Box p={{ base: 4, md: 6 }} textAlign="center" my="auto">
                <VStack spacing={4}>
                  <Spinner size="xl" color="brand.500" thickness="4px" />
                  <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
                    {activeDocument?.status === 'generating' 
                      ? 'Document is being generated. Please wait...'
                      : activeDocument?.status === 'not_started'
                      ? 'Document generation has not started yet.'
                      : 'Document not available yet.'}
                  </Text>
                </VStack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter flexDirection={{ base: "column", md: "row" }} gap={2} py={{ base: 2, md: 4 }} px={{ base: 3, md: 6 }} flexShrink={0} bg="white">
            <Button colorScheme="brand" variant="outline" onClick={onDocClose} w={{ base: "100%", md: "auto" }} order={{ base: 2, md: 1 }} size={{ base: "sm", md: "md" }}>
              Close
            </Button>
            {activeDocument && (
              <Button
                colorScheme={activeDocument.isPdf && downloadedDocs.has(activeDocument.id || activeDocument.title) ? "green" : "teal"}
                leftIcon={activeDocument.isPdf && downloadedDocs.has(activeDocument.id || activeDocument.title) ? <CheckCircleIcon /> : <DownloadIcon />}
                onClick={() => downloadDocumentAsPdf(activeDocument)}
                isLoading={downloadingDoc === activeDocument.title}
                isDisabled={!activeDocument.isPdf || !activeDocument.pdfFileId}
                w={{ base: "100%", md: "auto" }}
                order={{ base: 1, md: 2 }}
                size={{ base: "sm", md: "md" }}
                boxShadow="sm"
                sx={{ fontWeight: 500, letterSpacing: "normal" }}
              >
                {activeDocument.isPdf && downloadedDocs.has(activeDocument.id || activeDocument.title) ? "Downloaded" : "Download PDF"}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
